import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Filters from "components/Common/Filters"
import { Link, useLocation, useNavigate } from "react-router-dom"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import { useSelector } from "react-redux"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useDispatch } from "react-redux"
import {
  getAllOrders,
  syncOrderWithPos,
  updateOrderStatus,
} from "store/actions/orderAction"
import TableShimmer from "components/Shared/TableShimmer"
import { getAllBranchs } from "store/actions/usersAction"
import { exportData } from "store/actions/commonAction"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import DownloadCSV from "components/Shared/DownloadCSV"
import {
  getPerPageOptions,
  orderStatus,
  statusListStatus,
  todayDate,
} from "components/Common/constants"
import Select from "react-select"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import { setItemsPerPage } from "store/reducers/orderReducer"
import { getAppVersions } from "store/actions/customersAction"

const Orders = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Orders | Namq")

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const branchIdParam = searchParams.get("branchId")
  const orderTypeParam = searchParams.get("orderType")
  const platformParam = searchParams.get("platform")
  const minDate = searchParams.get("minDate") || todayDate
  const maxDate = searchParams.get("maxDate") || todayDate

  const {
    loading,
    isLoading,
    isUpdating,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    orders,
  } = useSelector(state => state.Orders)
  const { branchsList } = useSelector(state => state.Users)
  const { appVersions } = useSelector(state => state.Customers)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [searchTerm2, setSearchTerm2] = useState("")

  // const [appVersion, setAppVersion] = useState("")

  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    branchId: branchIdParam
      ? branchIdParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    status: parseInt(searchParams.get("status") || "0"),
    orderType: orderTypeParam
      ? orderTypeParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    platform: platformParam
      ? platformParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    appVersion: "",
  })

  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })
  const [showModal, setShowModal] = useState(false)

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "branchId",
      type: "multiSelect",
      options: [
        // { label: "All", value: "" },
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branch"),
    },
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 0 },
        { label: t("Sent To Branch"), value: 1 },
        { label: t("Start Preparing"), value: 2 },
        { label: t("Prepared"), value: 3 },
        { label: t("Collected"), value: 4 },
        { label: t("Cancelled"), value: 5 },
        { label: t("Sent To Branch Failed"), value: 6 },
      ],
      placeholder: t("Select Branch"),
    },
    {
      name: "orderType",
      type: "multiSelect",
      options: [
        { label: t("Pickup"), value: "pickup" },
        { label: t("Curbside"), value: "curbside" },
      ],
      placeholder: t("Select Order Type"),
    },
    {
      name: "platform",
      type: "multiSelect",
      options: [
        { label: t("Android"), value: "android" },
        { label: t("iOS"), value: "iOS" },
      ],
      placeholder: t("Platform"),
    },
  ]

  if (selectedFilters?.platform?.length > 0) {
    filters.push({
      name: "appVersion",
      type: "select",
      options: appVersions.map(item => ({
        label: item,
        value: item,
      })),
      placeholder: t("App Versions"),
    })
  }

  useEffect(() => {
    fetchAllOrders()
    fetchAllBranchs()
  }, [])

  const fetchAllOrders = () => {
    const { status, sortOrder, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform =
    platform?.flatMap(item =>
      item.value === "android" ? [item.value, "Android"] : item.value
    ) || []
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      orderNo: searchTerm2 || "",
      appVersion: platform?.length > 0 ? appVersion : "",
      status: status,
      branchId: branchIds,
      orderType: updatedOrderType,
      platform: updatedPlatform,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllOrders(payload))
    setIsApiCalled(true)
  }

  useEffect(() => {
    const { platform } = selectedFilters
    if (platform?.length > 0) {
      fetchAppVersions()
    }
  }, [selectedFilters])

  const fetchAppVersions = () => {
    const { platform } = selectedFilters
    const updatedPlatform = platform?.map(item => item.value) || []
    const transformedPlatform = updatedPlatform?.map(item =>
      item === "android" ? "Android" : item
    )
    const payload = { platform: transformedPlatform }
    dispatch(getAppVersions(payload))
  }

  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      9,
      "order_1",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      9,
      "order_2",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchChange2 = e => {
    setSearchTerm2(e.target.value)
  }

  const handleAppVersionChange = e => {
    // setAppVersion(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: selectedValue,
      }

      if (filterName === "platform" && selectedValue.length === 0) {
        updatedFilters.appVersion = "" // Reset appVersion if platform is cleared
      }

      return updatedFilters
    })
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const handleFetchOrders = (page = 1) => {
    const { status, sortOrder, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    // const updatedPlatform = platform?.map(item => item.value) || []
    const updatedPlatform =
      platform?.flatMap(item =>
        item.value === "android" ? [item.value, "Android"] : item.value
      ) || []

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (branchId?.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("branchId", branchIdString)
    }
    if (orderType?.length > 0) {
      const orderTypeString = orderType
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("orderType", orderTypeString)
    }
    if (platform?.length > 0) {
      const platformString = platform
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("platform", platformString)
    }

    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      orderNo: searchTerm2 || "",
      appVersion: platform?.length > 0 ? appVersion : "",
      branchId: branchIds,
      status: Number(status),
      orderType: updatedOrderType,
      platform: updatedPlatform,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getAllOrders(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/orders?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchOrders(1)
  }

  const onPageChange = page => {
    handleFetchOrders(page)
  }

  // useEffect(() => {
  //   const { platform } = selectedFilters
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters, itemsPerPage])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleDelete = () => {
    setShowDeleteModal(false)
  }

  const handleReset = () => {
    dispatch(setItemsPerPage(50))
    const payload = {
      perPage: 50,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      status: 0,
      dateRange: { min: todayDate, max: todayDate },
    }
    dispatch(getAllOrders(payload))
    setSelectedFilters({
      sortOrder: 2,
      branchId: [],
      status: 0,
      orderType: [],
      platform: [],
      appVersion: "",
    })
    // setAppVersion("")
    setSearchTerm("")
    setSearchTerm2("")
    setDateRange({ min: todayDate, max: todayDate })
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate([todayDate, todayDate])
    }
    navigate(`/orders?page=1`)
  }

  const formatPromiseDateTime = dateTimeString => {
    if (!dateTimeString) return "-"
    const date = new Date(dateTimeString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    let hours = date.getHours()
    const meridiem = hours >= 12 ? "PM" : "AM"
    hours = hours % 12 || 12 // Convert 0 to 12 for 12-hour clock
    const minutes = String(date.getMinutes()).padStart(2, "0")
    const seconds = String(date.getSeconds()).padStart(2, "0")

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${meridiem}`
  }

  const handleRowClick = id => {
    navigate(`/orders/order-details?id=${id}`)
  }

  const handleExcelDownload = async () => {
    const { status, branchId, orderType, platform } = selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform =
      platform?.flatMap(item =>
        item.value === "android" ? [item.value, "Android"] : item.value
      ) || []

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      orderNo: searchTerm2 || "",
      type: "ORDER",
      exportType: "xlsx",
      branchId: branchIds || [],
      userId: "",
      orderType: updatedOrderType,
      platform: updatedPlatform,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Order"))
    }
  }

  const exportToCSV = async () => {
    const { status, branchId, orderType, platform } = selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform =
      platform?.flatMap(item =>
        item.value === "android" ? [item.value, "Android"] : item.value
      ) || []

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      orderNo: searchTerm2 || "",
      type: "ORDER",
      exportType: "csv",
      branchId: branchIds || [],
      userId: "",
      orderType: updatedOrderType,
      platform: updatedPlatform,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Order"))
    }
  }

  // Convert bookingStatus to options array for react-select
  const statusOptions = Object.entries(orderStatus).map(([value, label]) => ({
    value,
    label: t(label),
  }))

  const handleStatusChange = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status,
    }
    dispatch(updateOrderStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setSelectedId(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleSentOnPOS = () => {
    const payload = {
      orderId: selectedId,
    }
    dispatch(syncOrderWithPos(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setSelectedId(null)
          handleFetchOrders()
          setShowModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const perPageOptions = getPerPageOptions(totalCount)

  const handlePerPageChange = e => {
    dispatch(setItemsPerPage(Number(e.target.value)))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                cardTitle={t("Orders List")}
                isAddBtn={false}
                isExportData={loading ? false : true}
                isCustomPage={true}
                perPageOptions={perPageOptions}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  // isSecondSearch={false}
                  // appVersion={appVersion}
                  // handleAppVersionChange={handleAppVersionChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  pageSizes={[0, 1, 2]}
                  itemsPerPage={50}
                  setItemsPerPage={null}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Name, Phone number")}
                  isSecondSearch={true}
                  searchTerm2={searchTerm2}
                  handleSearchChange2={handleSearchChange2}
                  searchBy2={t("Order No.")}
                  colSize={4}
                />
                {/* )} */}
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : orders.length === 0 ? (
                    <div className="g_center" style={{ minHeight: 200 }}>
                      <p className="text-center my-2 mb-2">
                        {t("No orders found")}
                      </p>
                    </div>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th style={{ whiteSpace: "nowrap" }}>
                            {t("S. No.")}
                          </th>
                          <th>{t("Order No")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Number")}</th>
                          {/* <th>Email</th> */}
                          <th>{t("Platform")}</th>
                          <th>{t("Amount")}</th>
                          <th>{t("Order Type")}</th>
                          <th>{t("Branch Name")}</th>
                          <th>{t("Date & Time")}</th>
                          <th>{t("Payment Method")}</th>
                          <th>{t("Status")}</th>
                          {usersPermissions.edit && (
                            <th style={{ minWidth: 200 }}>
                              {t("Update Status")}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              handleRowClick(item._id)
                            }}
                            className="cursor-pointer"
                          >
                            <th>
                              {" "}
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            {/* <th scope="row">
                              <Link
                                className="text-decoration-underline gray_text"
                                to={`/orders/order-details?id=${item._id}`}
                              >
                                {item._id}
                              </Link>
                            </th> */}
                            <td>{item.invoiceNo}</td>

                            <td>{item?.name || "-"}</td>
                            <td>{item?.phone || "-"}</td>
                            {/* <td>{item.email || "-"}</td> */}
                            <td>
                              {item.platform === "android" ||
                              item.platform === "Android"
                                ? t("Android")
                                : item.platform === "iOS"
                                ? t("iOS")
                                : "-"}
                              {item?.appVersion && ` (${item.appVersion})`}
                            </td>

                            <td>
                              {item.paidAmount} {item?.splitPayments?.currency}
                            </td>
                            <td className="text-capitalize">
                              {t(item.orderType)}
                            </td>
                            <td className="text-capitalize">
                              {language === "sa"
                                ? item?.branchNameAr
                                : item?.branchName}
                            </td>
                            <td>
                              {formatPromiseDateTime(item.promiseDateTime)}
                            </td>

                            <td>
                              <i
                                className={`${
                                  item.paymentMethod === "CARD"
                                    ? "fab fa-cc-mastercard me-1"
                                    : "" || item.paymentMethod === "APPLE"
                                    ? "fab bx bxl-apple me-1"
                                    : ""
                                }`}
                              ></i>
                              {t(item.paymentMethod)}
                            </td>

                            <td
                            // style={{
                            //   whiteSpace: "nowrap",
                            //   verticalAlign: "middle",
                            // }}
                            >
                              {/* SHOW SEND TO BRANCH FAILED IF HAVE POS_STATUS = ERROR */}
                              <span
                                style={{
                                  color:
                                    statusListStatus[
                                      item?.posSettings?.status === "Error"
                                        ? 6
                                        : item.orderStatus
                                    ].color,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item?.posSettings?.status === "Error"
                                  ? t("Sent To Branch Failed")
                                  : statusListStatus[item.orderStatus].text}
                              </span>
                            </td>
                            {/* <td>
                              {usersPermissions.list && (
                                <Button
                                  type="button"
                                  color="primary"
                                  className="btn-sm btn-rounded"
                                  onClick={() => {
                                    navigate(
                                      `/orders/order-details?id=${item._id}`
                                    )
                                  }}
                                >
                                  View Details
                                </Button>
                              )}
                            </td> */}

                            {usersPermissions.edit && (
                              <th onClick={e => e.stopPropagation()}>
                                {item?.posSettings?.status === "Error" ? (
                                  <Button
                                    color="primary"
                                    title={t("Sync Order With POS")}
                                    onClick={() => {
                                      setShowModal(true)
                                      setSelectedId(item._id)
                                    }}
                                  >
                                    {t("Send on POS")}
                                  </Button>
                                ) : (
                                  <Select
                                    value={statusOptions.find(
                                      option =>
                                        option.value ===
                                        item.orderStatus.toString()
                                    )}
                                    isDisabled={
                                      selectedId === item._id && isLoading
                                    }
                                    onChange={(selectedOption, event) => {
                                      if (event.nativeEvent) {
                                        event.nativeEvent.stopPropagation()
                                      }
                                      handleStatusChange(
                                        item._id,
                                        selectedOption.value
                                      )
                                    }}
                                    options={statusOptions
                                      .filter(option => {
                                        const currentStatus = parseInt(
                                          item.orderStatus
                                        )
                                        const optionValue = parseInt(
                                          option.value
                                        )

                                        switch (currentStatus) {
                                          case 1:
                                            return (
                                              optionValue === 2 ||
                                              optionValue === 5
                                            )
                                          case 2:
                                            return (
                                              optionValue === 3 ||
                                              optionValue === 5
                                            )
                                          case 3:
                                            return (
                                              optionValue === 4 ||
                                              optionValue === 5
                                            )
                                          case 4:
                                            return false
                                          case 5:
                                            return false
                                          default:
                                            return false
                                        }
                                      })
                                      .map(option => ({
                                        ...option,
                                        isDisabled:
                                          parseInt(option.value) ===
                                          parseInt(item.orderStatus),
                                      }))}
                                    placeholder={t("Update Status")}
                                  />
                                )}
                              </th>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && orders.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={orders}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <Modal size="md" isOpen={showModal} centered={true}>
        <div className="modal-content">
          <ModalBody className="px-4 py-5 text-center">
            <button
              type="button"
              onClick={() => {
                setShowModal(false)
                setSelectedId(null)
              }}
              className="btn-close position-absolute end-0 top-0 m-3"
            ></button>
            <div className="avatar-sm mb-4 mx-auto">
              <div className="avatar-title bg-primary text-primary bg-opacity-10 font-size-20 rounded-3">
                <i className="bx bx-sync"></i>
              </div>
            </div>
            <p className="text-muted font-size-16 mb-4">
              {t("Are you sure you want to Sync order with POS")}
            </p>

            <div className="hstack gap-2 justify-content-center mb-0">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false)
                  setSelectedId(null)
                }}
              >
                {t("No")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSentOnPOS}
                disabled={isLoading}
                title={t("Sync Order With POS")}
              >
                {t("Yes")}
              </button>
            </div>
          </ModalBody>
        </div>
      </Modal>
    </div>
  )
}

export default Orders
